import { useState, useEffect, useRef } from 'react';
import {
  useReadContract,
  useReadContracts,
  useWriteContract,
  useWaitForTransactionReceipt,
  useChainId,
  useAccount,
} from 'wagmi';
import { parseUnits, formatUnits, type Hex } from 'viem';
import { toast } from 'sonner';
import {
  Coins,
  RefreshCw,
  Send,
  Wallet,
  Users,
  Download,
  CheckCircle2,
  TreePine,
  Copy,
  Upload,
  AlertTriangle,
  Info,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/ui/card';
import { Button } from '@/shared/ui/button';
import { Badge } from '@/shared/ui/badge';
import { Skeleton } from '@/shared/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/ui/table';
import { contracts, galactica, galacticaCassiopeia } from '@/shared/config';
import { useContractSettings, getCurrentEpoch, DEFAULT_EMISSION_EPOCHS } from '@/shared/hooks/useContractSettings';
import { erc20Abi, rewardDistributorAbi } from '@/shared/config/abis';
import { orpc } from '@/shared/api/client';
import { Link } from 'react-router';
import { buildMerkleTree, type MerkleProof } from '@/shared/lib/merkle-tree';
import { Instructions, Step } from '@/shared/ui/instructions';

/**
 * Calculate UBI Reputation: points × log₁₀(veGNET + 1)
 *
 * We add 1 to veGNET before taking log₁₀ so the result is always non-negative:
 * - veGNET = 0: log₁₀(1) = 0 → reputation = 0
 * - veGNET = 0.156: log₁₀(1.156) ≈ 0.063 → positive reputation
 * - veGNET = 9: log₁₀(10) = 1 → reputation = points × 1
 * - veGNET = 99: log₁₀(100) = 2 → reputation = points × 2
 */
function calculateUBIReputation(points: number, vegnet: number): number {
  if (vegnet <= 0) return 0;
  return points * Math.log10(vegnet + 1);
}

interface DistributionUser {
  entryId: string;
  walletAddress: string | null;
  points: number;
  vegnet: number;
  ubiReputation: number;  // points × log10(veGNET)
  dailyReward: string;
  accumulatedReward: string;
  distributedAmount: string;
  toDistribute: number;  // Calculated from UBI reputation share
  pool: string;
  rank: number;
  selected: boolean;
}

function StatCard({
  title,
  value,
  icon: Icon,
  isLoading,
}: {
  title: string;
  value: string;
  icon: React.ComponentType<{ className?: string }>;
  isLoading?: boolean;
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm font-medium text-[#807f7e]">{title}</CardTitle>
        <Icon className="w-5 h-5 text-[#807f7e]" />
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-8 w-32" />
        ) : (
          <p className="text-2xl font-bold text-[#2b2b2b]">{value}</p>
        )}
      </CardContent>
    </Card>
  );
}

export function DistributionPage() {
  const chainId = useChainId();
  const { address: walletAddress, isConnected } = useAccount();
  const currentChain = chainId === galactica.id ? galactica : galacticaCassiopeia;

  const indexPoolAddress = contracts.indexPool[chainId as keyof typeof contracts.indexPool];
  const gubiAddress = contracts.gubi[chainId as keyof typeof contracts.gubi];

  const [users, setUsers] = useState<DistributionUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [onlyWithWallet, setOnlyWithWallet] = useState(true);
  const [onlyWithVeGNET, setOnlyWithVeGNET] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 50;
  const [syncStatus, setSyncStatus] = useState<{
    totalUsers: number;
    usersWithWallets: number;
    pendingSync: number;
    lastSyncJob: { status: string; completedAt: number | null } | null;
  } | null>(null);

  // Get gUBI balance of connected wallet
  const { data: gubiBalance } = useReadContract({
    address: gubiAddress as `0x${string}`,
    abi: erc20Abi,
    functionName: 'balanceOf',
    args: walletAddress ? [walletAddress] : undefined,
    query: { enabled: !!gubiAddress && !!walletAddress },
  });

  // Get gUBI total supply
  const { data: gubiTotalSupply } = useReadContract({
    address: gubiAddress as `0x${string}`,
    abi: erc20Abi,
    functionName: 'totalSupply',
    query: { enabled: !!gubiAddress },
  });

  // Merkle tree state
  const [isGenerating, setIsGenerating] = useState(false);
  const [merkleRoot, setMerkleRoot] = useState<Hex | null>(null);
  const [merkleProofs, setMerkleProofs] = useState<MerkleProof[]>([]);

  // Get contract settings from database
  const { settings: contractSettings, lastGubiDistributionDate, emissionEpochs } = useContractSettings();
  const rewardDistributorAddress = contractSettings.rewardDistributor as `0x${string}` | undefined;

  // Get current epoch's emission rate
  const currentEmissionEpoch = getCurrentEpoch(emissionEpochs);
  const annualEmission = currentEmissionEpoch?.annualEmission ?? 0;
  const dailyEmission = annualEmission / 365;

  // Calculate days since last distribution and period emission
  const now = Date.now();
  const daysSinceLastDistribution = lastGubiDistributionDate
    ? (now - lastGubiDistributionDate) / (1000 * 60 * 60 * 24)
    : 30; // Default to 30 days if never distributed
  const periodEmission = dailyEmission * daysSinceLastDistribution;

  // Publishing state
  const [isPublishing, setIsPublishing] = useState(false);
  const [isStoringProofs, setIsStoringProofs] = useState(false);
  const [publishedEpoch, setPublishedEpoch] = useState<number | null>(null);

  // Read current epoch from RewardDistributor
  const { data: contractData, refetch: refetchContractData } = useReadContracts({
    contracts: rewardDistributorAddress ? [
      {
        address: rewardDistributorAddress,
        abi: rewardDistributorAbi,
        functionName: 'currentEpoch',
      },
      {
        address: rewardDistributorAddress,
        abi: rewardDistributorAbi,
        functionName: 'rewardMerkleRoot',
      },
      {
        address: rewardDistributorAddress,
        abi: rewardDistributorAbi,
        functionName: 'owner',
      },
      {
        address: rewardDistributorAddress,
        abi: rewardDistributorAbi,
        functionName: 'pendingOwner',
      },
    ] : [],
    query: { enabled: !!rewardDistributorAddress },
  });

  const currentEpoch = contractData?.[0]?.result as bigint | undefined;
  const currentMerkleRoot = contractData?.[1]?.result as Hex | undefined;
  const contractOwner = contractData?.[2]?.result as `0x${string}` | undefined;
  const pendingOwner = contractData?.[3]?.result as `0x${string}` | undefined;

  // Check if connected wallet is owner
  const isOwner = walletAddress && contractOwner && walletAddress.toLowerCase() === contractOwner.toLowerCase();
  const isPendingOwner = walletAddress && pendingOwner && pendingOwner !== '0x0000000000000000000000000000000000000000' && walletAddress.toLowerCase() === pendingOwner.toLowerCase();

  // Write contract for updating merkle root
  const { writeContract, isPending: isWritePending, data: txHash } = useWriteContract();
  const { isLoading: isTxLoading, isSuccess: isTxSuccess } = useWaitForTransactionReceipt({
    hash: txHash,
  });

  // Track which tx we've already handled
  const handledTxRef = useRef<string | null>(null);

  // Handle successful transaction - store proofs in D1
  useEffect(() => {
    if (isTxSuccess && txHash && handledTxRef.current !== txHash && merkleRoot && merkleProofs.length > 0) {
      handledTxRef.current = txHash;
      setIsStoringProofs(true);

      // Get the new epoch (current + 1 since updateRewardMerkleRoot increments it)
      const newEpoch = currentEpoch ? Number(currentEpoch) + 1 : 1;

      // Store proofs in D1 and update lastGubiDistributionDate
      orpc.distribution.storeProofs({
        chainId,
        epoch: newEpoch,
        root: merkleRoot,
        proofs: merkleProofs.map((p) => ({
          wallet: p.account,
          leafIndex: p.leafIndex,
          amount: p.amount.toString(),
          proof: p.proof as string[],
        })),
      })
        .then(async () => {
          // Update lastGubiDistributionDate in settings
          const updatedConfig = {
            ...contractSettings.config,
            lastGubiDistributionDate: Date.now(),
          };
          await orpc.settings.save({
            chainId,
            settings: {
              rewardDistributor: contractSettings.rewardDistributor,
              gnetRewardDistributor: contractSettings.gnetRewardDistributor,
              gubi: contractSettings.gubi,
              votingEscrow: contractSettings.votingEscrow,
              indexPool: contractSettings.indexPool,
              config: updatedConfig,
            },
          });
          setPublishedEpoch(newEpoch);
          toast.success(`Distribution published! Epoch ${newEpoch}`);
          refetchContractData();
        })
        .catch((err) => {
          console.error('Failed to store proofs:', err);
          toast.error('Transaction confirmed but failed to store proofs. Export JSON as backup.');
        })
        .finally(() => {
          setIsStoringProofs(false);
          setIsPublishing(false);
        });
    }
  }, [isTxSuccess, txHash, merkleRoot, merkleProofs, currentEpoch, refetchContractData, chainId, contractSettings]);

  const isPending = isGenerating || isWritePending || isTxLoading || isStoringProofs;

  // Handle publish - update merkle root on-chain and store proofs in D1
  const handlePublish = () => {
    if (!merkleRoot || !rewardDistributorAddress) {
      toast.error('Generate merkle tree and configure RewardDistributor address first');
      return;
    }

    if (!isConnected) {
      toast.error('Connect your wallet first');
      return;
    }

    setIsPublishing(true);
    writeContract({
      address: rewardDistributorAddress,
      abi: rewardDistributorAbi,
      functionName: 'updateRewardMerkleRoot',
      args: [merkleRoot],
    });
  };

  // Handle distribution - generate Merkle tree
  const handleGenerateMerkle = async () => {
    // Use selected users if any, otherwise use all filtered users
    const usersToDistribute = (selectedUsers.length > 0
      ? selectedUsers
      : filteredUsers
    ).filter((u) => u.walletAddress && u.toDistribute > 0);

    if (usersToDistribute.length === 0) {
      toast.error('No users with wallet addresses to distribute to');
      return;
    }

    setIsGenerating(true);

    try {
      // Build leaves for Merkle tree
      const leaves = usersToDistribute.map((user, index) => ({
        leafIndex: index,
        account: user.walletAddress!,
        amount: parseUnits(user.toDistribute.toFixed(6), 18),
      }));

      // Generate Merkle tree
      const { root, proofs } = buildMerkleTree(leaves);

      setMerkleRoot(root);
      setMerkleProofs(proofs);

      toast.success(`Merkle tree generated for ${usersToDistribute.length} users`);
    } catch (err) {
      console.error('Failed to generate Merkle tree:', err);
      toast.error('Failed to generate Merkle tree');
    } finally {
      setIsGenerating(false);
    }
  };

  // Copy to clipboard helper
  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  // Export Merkle data as JSON
  const exportMerkleData = () => {
    if (!merkleRoot || merkleProofs.length === 0) {
      toast.error('Generate Merkle tree first');
      return;
    }

    const data = {
      root: merkleRoot,
      generatedAt: new Date().toISOString(),
      totalUsers: merkleProofs.length,
      proofs: merkleProofs,
    };

    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `merkle-distribution-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Merkle data exported');
  };

  // Fetch users from API (wallets now come pre-resolved from background sync)
  const fetchUsers = async () => {
    setLoading(true);
    try {
      // Fetch all users by paginating through all pages
      const PAGE_SIZE = 500;
      let allUsers: Array<{
        entryId: string;
        points: number;
        vegnet: string | null;
        walletAddresses: string[] | null;
        primaryWallet: string | null;  // Wallet with highest veGNET
        dailyReward: string;
        accumulatedReward: string;
        distributedAmount: string;
        pool: string;
        rank: number;
      }> = [];

      let page = 1;
      let hasMore = true;

      while (hasMore) {
        const result = await orpc.users.list({ page, pageSize: PAGE_SIZE });
        allUsers = [...allUsers, ...result.users];
        hasMore = result.users.length === PAGE_SIZE;
        page++;
      }

      // First pass: calculate UBI reputation for each user
      // veGNET is stored as JSON keyed by chainId: {"613419": "0.117", "843843": "0.624"}
      // Legacy format was just a number string: "0.623458"
      const usersWithReputation = allUsers.map((user) => {
        const points = user.points || 0;
        let vegnet = 0;
        if (user.vegnet) {
          try {
            const parsed = JSON.parse(user.vegnet);
            if (typeof parsed === 'object' && parsed !== null) {
              // New format: {"613419": "0.117", "843843": "0.624"}
              vegnet = parseFloat(parsed[String(chainId)] || '0') || 0;
            } else {
              // Legacy format: just a number (JSON.parse("0.623") returns 0.623)
              vegnet = typeof parsed === 'number' ? parsed : 0;
            }
          } catch {
            // Fallback: try direct parseFloat
            vegnet = parseFloat(user.vegnet) || 0;
          }
        }
        const ubiReputation = calculateUBIReputation(points, vegnet);
        return { ...user, points, vegnet, ubiReputation };
      });

      // Calculate total network reputation
      const totalReputation = usersWithReputation.reduce(
        (sum, u) => sum + u.ubiReputation,
        0
      );

      // Second pass: calculate rewards based on share of period emission
      const distributionUsers: DistributionUser[] = usersWithReputation.map((user) => {
        const share = totalReputation > 0 ? user.ubiReputation / totalReputation : 0;
        const toDistribute = share * periodEmission;

        return {
          entryId: user.entryId,
          // Use primary wallet (highest veGNET), fallback to first wallet
          walletAddress: user.primaryWallet ?? user.walletAddresses?.[0] ?? null,
          points: user.points,
          vegnet: user.vegnet,
          ubiReputation: user.ubiReputation,
          dailyReward: user.dailyReward,
          accumulatedReward: user.accumulatedReward,
          distributedAmount: user.distributedAmount,
          toDistribute,
          pool: user.pool,
          rank: user.rank,
          selected: false,
        };
      });

      setUsers(distributionUsers);
    } catch (err) {
      toast.error('Failed to fetch users');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch sync status
  const fetchSyncStatus = async () => {
    try {
      const status = await orpc.sync.status({});
      setSyncStatus(status);
    } catch (err) {
      console.error('Failed to fetch sync status', err);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchSyncStatus();
  }, [chainId, lastGubiDistributionDate]);  // Re-fetch when chain or distribution date changes

  // Filter users by wallet status and veGNET
  const filteredUsers = users.filter((u) => {
    if (onlyWithWallet && !u.walletAddress) return false;
    if (onlyWithVeGNET && u.vegnet <= 0) return false;
    return true;
  });

  // Pagination
  const totalPages = Math.ceil(filteredUsers.length / PAGE_SIZE);
  const paginatedUsers = filteredUsers.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [onlyWithWallet, onlyWithVeGNET]);

  // Selected users
  const selectedUsers = filteredUsers.filter((u) => u.selected);
  const totalToDistribute = selectedUsers.reduce(
    (sum, u) => sum + u.toDistribute,
    0
  );

  // Toggle selection
  const toggleSelect = (entryId: string) => {
    setUsers((prev) =>
      prev.map((u) =>
        u.entryId === entryId ? { ...u, selected: !u.selected } : u
      )
    );
  };

  // Select all in current filter
  const selectAll = () => {
    const filteredIds = new Set(filteredUsers.map((u) => u.entryId));
    setUsers((prev) =>
      prev.map((u) => ({
        ...u,
        selected: filteredIds.has(u.entryId),
      }))
    );
  };

  // Deselect all
  const deselectAll = () => {
    setUsers((prev) => prev.map((u) => ({ ...u, selected: false })));
  };

  // Export distribution list as CSV
  const exportCSV = () => {
    const csvContent = [
      ['Entry ID', 'Pool', 'Rank', 'Points', 'veGNET', 'UBI Reputation', 'To Distribute (gUBI)', 'Wallet Address'],
      ...filteredUsers.map((u) => [
        u.entryId,
        u.pool,
        u.rank,
        u.points,
        u.vegnet.toFixed(2),
        u.ubiReputation.toFixed(2),
        u.toDistribute.toFixed(2),
        u.walletAddress || 'Not resolved',
      ]),
    ]
      .map((row) => row.join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `distribution-gubi-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV exported');
  };

  const formatLargeNumber = (value: bigint | undefined, decimals = 18) => {
    if (!value) return '0';
    const num = Number(formatUnits(value, decimals));
    // Use compact notation for large numbers (100M, 1.5B, etc.)
    if (num >= 1_000_000) {
      return new Intl.NumberFormat('en', {
        notation: 'compact',
        maximumFractionDigits: 1
      }).format(num);
    }
    return num.toLocaleString(undefined, { maximumFractionDigits: 2 });
  };

  const explorerUrl = currentChain.blockExplorers?.default.url || '';

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#2b2b2b]">gUBI Distribution</h1>
          <p className="text-[#807f7e]">Distribute gUBI rewards based on UBI Reputation</p>
          {rewardDistributorAddress && (
            <div className="flex items-center gap-2 mt-1">
              <span className="text-xs text-[#807f7e]">Contract:</span>
              <a
                href={`${currentChain.blockExplorers?.default.url}/address/${rewardDistributorAddress}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs font-mono text-[#f26f56] hover:underline"
              >
                {rewardDistributorAddress.slice(0, 6)}...{rewardDistributorAddress.slice(-4)}
              </a>
            </div>
          )}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchUsers}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button variant="outline" onClick={exportCSV}>
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Connection warning */}
      {!isConnected && (
        <Card className="border-yellow-200 bg-yellow-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Wallet className="w-5 h-5 text-yellow-600" />
              <p className="text-yellow-800">Connect your wallet to distribute rewards</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Owner check warning */}
      {isConnected && rewardDistributorAddress && contractOwner && !isOwner && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
                <div className="flex-1">
                  <p className="text-red-800 font-medium">You are not the contract owner</p>
                  <p className="text-red-700 text-sm mt-1">
                    Only the owner can update the merkle root. Your wallet cannot sign this transaction.
                  </p>
                </div>
              </div>
              <div className="bg-white rounded-lg p-3 text-sm space-y-2">
                <div className="flex justify-between">
                  <span className="text-[#807f7e]">Contract Owner</span>
                  <a
                    href={`${explorerUrl}/address/${contractOwner}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-mono text-[#f26f56] hover:underline"
                  >
                    {contractOwner.slice(0, 6)}...{contractOwner.slice(-4)}
                  </a>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#807f7e]">Your Wallet</span>
                  <span className="font-mono">{walletAddress?.slice(0, 6)}...{walletAddress?.slice(-4)}</span>
                </div>
                {pendingOwner && pendingOwner !== '0x0000000000000000000000000000000000000000' && (
                  <div className="flex justify-between">
                    <span className="text-[#807f7e]">Pending Owner</span>
                    <span className="font-mono text-blue-600">{pendingOwner.slice(0, 6)}...{pendingOwner.slice(-4)}</span>
                  </div>
                )}
              </div>
              <div className="text-sm text-red-700">
                <p className="font-medium">To grant access:</p>
                <ol className="list-decimal list-inside mt-1 space-y-1 text-red-600">
                  <li>Current owner calls <code className="bg-red-100 px-1 rounded">transferOwnership({walletAddress?.slice(0, 6)}...{walletAddress?.slice(-4)})</code></li>
                  <li>You click "Accept Ownership" below</li>
                </ol>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Accept ownership button */}
      {isPendingOwner && (
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="w-5 h-5 text-blue-600" />
                <div>
                  <p className="text-blue-800 font-medium">Ownership transfer pending</p>
                  <p className="text-blue-700 text-sm">You have been granted ownership. Click to accept.</p>
                </div>
              </div>
              <Button
                onClick={() => {
                  writeContract({
                    address: rewardDistributorAddress!,
                    abi: rewardDistributorAbi,
                    functionName: 'acceptOwnership',
                    args: [],
                  });
                }}
                disabled={isWritePending || isTxLoading}
              >
                {isWritePending || isTxLoading ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Accepting...
                  </>
                ) : (
                  'Accept Ownership'
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Instructions */}
      <Instructions title="gUBI Reward Calculation">
        <Step number={1} title="UBI Reputation Formula">
          UBI_Reputation = Points × log₁₀(veGNET + 1)
          <ul className="text-xs text-[#807f7e] mt-1 ml-4 list-disc">
            <li>veGNET = 0: log₁₀(1) = 0 → reputation = 0</li>
            <li>veGNET = 0.15: log₁₀(1.15) ≈ 0.06 → small positive reputation</li>
            <li>veGNET = 9: log₁₀(10) = 1 → reputation = Points</li>
            <li>veGNET = 99: log₁₀(100) = 2 → reputation = 2×Points</li>
          </ul>
        </Step>
        <Step number={2} title="Period Emission">
          <div className="space-y-1">
            {currentEmissionEpoch ? (
              <div>
                Current epoch (since {new Date(currentEmissionEpoch.startDate).toLocaleDateString()}): {dailyEmission.toLocaleString(undefined, { maximumFractionDigits: 2 })} gUBI/day ({annualEmission.toLocaleString()} gUBI/year)
              </div>
            ) : (
              <div className="text-[#f59e0b]">
                No active emission epoch. Configure epochs in <Link to="/settings" className="underline">Settings</Link>.
              </div>
            )}
            <div className="font-medium text-[#2b2b2b]">
              Days since last distribution: {daysSinceLastDistribution.toFixed(2)} days
              {lastGubiDistributionDate && (
                <span className="text-[#807f7e] font-normal"> (last: {new Date(lastGubiDistributionDate).toLocaleDateString()})</span>
              )}
              {!lastGubiDistributionDate && (
                <span className="text-[#f59e0b] font-normal"> (never distributed - using 30 days default)</span>
              )}
            </div>
            <div className="font-medium text-[#f26f56]">
              Period emission: {periodEmission.toLocaleString(undefined, { maximumFractionDigits: 2 })} gUBI
            </div>
          </div>
        </Step>
        <Step number={3} title="Distribution Process">
          Select users → Generate Merkle Tree → Publish on-chain → Fund contract with gUBI.
        </Step>
        <p className="text-xs text-[#807f7e] mt-2">
          veGNET synced every 2 min from Voting Escrow. Shows balance for <strong>connected chain only</strong>.
        </p>
      </Instructions>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard
          title="gUBI Token"
          value={gubiAddress ? `${gubiAddress.slice(0, 6)}...${gubiAddress.slice(-4)}` : 'Not configured'}
          icon={Coins}
        />
        <StatCard
          title="Your gUBI Balance"
          value={gubiBalance !== undefined ? `${formatLargeNumber(gubiBalance as bigint)} gUBI` : '—'}
          icon={Wallet}
          isLoading={isConnected && gubiAddress && gubiBalance === undefined}
        />
        <StatCard
          title="Total gUBI Supply"
          value={gubiTotalSupply !== undefined ? `${formatLargeNumber(gubiTotalSupply as bigint)} gUBI` : '—'}
          icon={Coins}
          isLoading={gubiAddress && gubiTotalSupply === undefined}
        />
        <StatCard
          title="Users to Distribute"
          value={syncStatus?.totalUsers.toLocaleString() ?? users.length.toLocaleString()}
          icon={Users}
          isLoading={loading}
        />
      </div>

      {/* Wallet Status */}
      {syncStatus && (
        <div className="flex items-center justify-between p-3 bg-white border rounded-lg">
          <div className="flex items-center gap-4">
            <span className="text-sm text-[#807f7e]">Wallets:</span>
            <Badge variant="outline" className="bg-green-50 text-green-700">
              {syncStatus.usersWithWallets} resolved
            </Badge>
            {syncStatus.pendingSync > 0 && (
              <Badge variant="outline" className="bg-yellow-50 text-yellow-700">
                {syncStatus.pendingSync} no wallet
              </Badge>
            )}
            <span className="text-sm text-[#807f7e]">
              ({((syncStatus.usersWithWallets / (syncStatus.usersWithWallets + syncStatus.pendingSync)) * 100).toFixed(1)}% have wallets)
            </span>
          </div>
          <div className="text-xs text-[#807f7e]">
            Wallets synced from Claimr webhooks
            {syncStatus.lastSyncJob?.completedAt && (
              <span className="ml-2">
                · Last sync: {new Date(syncStatus.lastSyncJob.completedAt * 1000).toLocaleString()}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Distribution Panel */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Send className="w-5 h-5" />
                Distribution Queue
              </CardTitle>
              <CardDescription>Select users and distribute gUBI rewards</CardDescription>
            </div>
            <div className="flex items-center gap-4">
              {/* Wallet filter */}
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={onlyWithWallet}
                  onChange={(e) => setOnlyWithWallet(e.target.checked)}
                  className="rounded"
                />
                Only with wallet
              </label>
              {/* veGNET filter */}
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={onlyWithVeGNET}
                  onChange={(e) => setOnlyWithVeGNET(e.target.checked)}
                  className="rounded"
                />
                Only with veGNET
              </label>
              <Button variant="outline" size="sm" onClick={selectAll}>
                Select All
              </Button>
              <Button variant="outline" size="sm" onClick={deselectAll}>
                Deselect All
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Selection summary / Generate action */}
          {(() => {
            const distributableUsers = filteredUsers.filter(u => u.walletAddress && u.toDistribute > 0);
            const hasSelection = selectedUsers.length > 0;
            const usersForTree = hasSelection ? selectedUsers.filter(u => u.walletAddress && u.toDistribute > 0) : distributableUsers;
            const totalForTree = usersForTree.reduce((sum, u) => sum + u.toDistribute, 0);

            if (distributableUsers.length === 0) return null;

            return (
              <div className="mb-4 p-4 bg-[#f26f56]/10 rounded-lg flex items-center justify-between">
                <div className="flex items-center gap-4">
                  {hasSelection ? (
                    <>
                      <CheckCircle2 className="w-5 h-5 text-[#f26f56]" />
                      <span>
                        <strong>{usersForTree.length}</strong> users selected
                      </span>
                    </>
                  ) : (
                    <>
                      <Info className="w-5 h-5 text-[#f26f56]" />
                      <span>
                        <strong>{distributableUsers.length}</strong> users ready to distribute
                      </span>
                    </>
                  )}
                  <span className="text-[#807f7e]">|</span>
                  <span>
                    Total: <strong>{totalForTree.toLocaleString(undefined, { maximumFractionDigits: 2 })} gUBI</strong>
                  </span>
                </div>
                <Button
                  onClick={handleGenerateMerkle}
                  disabled={isPending || usersForTree.length === 0}
                >
                  {isPending ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <TreePine className="w-4 h-4 mr-2" />
                      Generate Merkle Tree{hasSelection ? ` (${usersForTree.length} selected)` : ` (All ${distributableUsers.length})`}
                    </>
                  )}
                </Button>
              </div>
            );
          })()}

          {/* Merkle Root Display */}
          {merkleRoot && (
            <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <TreePine className="w-5 h-5 text-green-600" />
                  <span className="font-medium text-green-800">Merkle Tree Generated</span>
                  {publishedEpoch && (
                    <Badge className="bg-green-600 text-white">Published (Epoch {publishedEpoch})</Badge>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={exportMerkleData}>
                    <Download className="w-4 h-4 mr-1" />
                    Export JSON
                  </Button>
                  {rewardDistributorAddress ? (
                    <Button
                      size="sm"
                      onClick={handlePublish}
                      disabled={isPending || !isConnected || publishedEpoch !== null}
                    >
                      {isWritePending || isTxLoading ? (
                        <>
                          <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
                          {isTxLoading ? 'Confirming...' : 'Publishing...'}
                        </>
                      ) : isStoringProofs ? (
                        <>
                          <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
                          Storing proofs...
                        </>
                      ) : publishedEpoch ? (
                        <>
                          <CheckCircle2 className="w-4 h-4 mr-1" />
                          Published
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4 mr-1" />
                          Publish Distribution
                        </>
                      )}
                    </Button>
                  ) : (
                    <Link to="/settings">
                      <Button size="sm" variant="outline">
                        <AlertTriangle className="w-4 h-4 mr-1" />
                        Configure Contract
                      </Button>
                    </Link>
                  )}
                </div>
              </div>
              <div className="space-y-2">
                <div>
                  <p className="text-xs text-green-700 mb-1">Merkle Root</p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 text-sm bg-white p-2 rounded border font-mono break-all">
                      {merkleRoot}
                    </code>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => copyToClipboard(merkleRoot, 'Merkle root')}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <p className="text-xs text-green-600">
                  {merkleProofs.length} proofs generated for cumulative rewards.
                </p>

                {/* Contract Status */}
                {rewardDistributorAddress && (
                  <div className="mt-3 p-2 bg-white rounded border text-xs space-y-1">
                    <div className="flex justify-between">
                      <span className="text-[#807f7e]">RewardDistributor</span>
                      <a
                        href={`${explorerUrl}/address/${rewardDistributorAddress}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-mono text-[#f26f56] hover:underline"
                      >
                        {rewardDistributorAddress.slice(0, 6)}...{rewardDistributorAddress.slice(-4)}
                      </a>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#807f7e]">Current Epoch</span>
                      <span className="font-medium">{currentEpoch !== undefined ? Number(currentEpoch) : '—'}</span>
                    </div>
                    {currentMerkleRoot && currentMerkleRoot !== '0x0000000000000000000000000000000000000000000000000000000000000000' && (
                      <div className="flex justify-between">
                        <span className="text-[#807f7e]">Current Root</span>
                        <code className="font-mono">{currentMerkleRoot.slice(0, 10)}...{currentMerkleRoot.slice(-8)}</code>
                      </div>
                    )}
                  </div>
                )}

                {!rewardDistributorAddress && (
                  <p className="text-xs text-yellow-700 bg-yellow-50 p-2 rounded flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    RewardDistributor not configured. <Link to="/settings" className="underline">Add it in Settings</Link> to enable on-chain publishing.
                  </p>
                )}

                {publishedEpoch && (
                  <p className="text-xs text-blue-700 bg-blue-50 p-2 rounded">
                    Distribution published to Epoch {publishedEpoch}. Remember to fund the RewardDistributor contract with gUBI tokens so users can claim.
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Users table */}
          {loading ? (
            <div className="space-y-2">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : (
            <div className="border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">
                      <input
                        type="checkbox"
                        checked={filteredUsers.length > 0 && filteredUsers.every((u) => u.selected)}
                        onChange={() => {
                          if (filteredUsers.every((u) => u.selected)) {
                            deselectAll();
                          } else {
                            selectAll();
                          }
                        }}
                        className="rounded"
                      />
                    </TableHead>
                    <TableHead>Entry ID</TableHead>
                    <TableHead className="text-right">Points</TableHead>
                    <TableHead className="text-right">veGNET</TableHead>
                    <TableHead className="text-right">UBI Rep.</TableHead>
                    <TableHead className="text-right">To Distribute</TableHead>
                    <TableHead>Wallet</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedUsers.map((user) => {
                    const hasVeGNET = user.vegnet > 0;
                    return (
                      <TableRow
                        key={user.entryId}
                        className={!hasVeGNET ? 'opacity-50 bg-gray-50' : ''}
                      >
                        <TableCell>
                          <input
                            type="checkbox"
                            checked={user.selected}
                            onChange={() => toggleSelect(user.entryId)}
                            className="rounded"
                            disabled={!hasVeGNET}
                          />
                        </TableCell>
                        <TableCell className="font-mono text-sm">
                          {user.entryId.includes('::') ? user.entryId.split('::')[0] : user.entryId}
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {user.points.toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right font-mono text-sm">
                          {hasVeGNET ? (
                            user.vegnet.toLocaleString(undefined, { maximumFractionDigits: 2 })
                          ) : (
                            <span className="text-[#807f7e]">0</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right font-mono text-sm">
                          {user.ubiReputation.toFixed(1)}
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {hasVeGNET ? `${user.toDistribute.toFixed(2)} gUBI` : '—'}
                        </TableCell>
                        <TableCell>
                          {user.walletAddress ? (
                            <a
                              href={`${explorerUrl}/address/${user.walletAddress}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm font-mono text-[#f26f56] hover:underline"
                            >
                              {user.walletAddress.slice(0, 6)}...{user.walletAddress.slice(-4)}
                            </a>
                          ) : (
                            <span className="text-[#807f7e] text-sm">Not resolved</span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              {totalPages > 1 && (
                <div className="p-3 flex items-center justify-between border-t">
                  <span className="text-sm text-[#807f7e]">
                    Showing {(currentPage - 1) * PAGE_SIZE + 1}-{Math.min(currentPage * PAGE_SIZE, filteredUsers.length)} of {filteredUsers.length} users
                  </span>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </Button>
                    <span className="text-sm text-[#807f7e] px-2">
                      Page {currentPage} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
